import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { deviceQuickDetailsQuery, type DeviceQuickDetailsPage } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Inventoree::deviceQuickDetails - Hardware summary din Easeedesk
 * (inventoree/device/quickDetails.php). Graficul AmCharts e inlocuit cu
 * acelasi cuvant pe care il scria axa System (Very Slow / Slow / Fair / Fast).
 */

export function statusWord(status: number | null | undefined): string {
  if (status === 1) return 'Very Slow'
  if (status === 2) return 'Slow'
  if (status === 3) return 'Fair'
  if (status !== null && status !== undefined && status > 3) return 'Fast'
  return ''
}

export function DeviceQuickDetails({ id }: ScreenProps) {
  const { data, error } = useQuery(deviceQuickDetailsQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <QuickDetails page={data} />
}

function na(value: string | null | undefined, slanted = false) {
  if (value) return value
  return slanted ? <em>n/a</em> : 'n/a'
}

function QuickDetails({ page }: { page: DeviceQuickDetailsPage }) {
  const word = statusWord(page.status)
  return (
    <div className="row device-quick-details">
      <div className="col-md-12">
        <h5 className="bordered">Hardware summary</h5>
      </div>

      <div className="col-md-6">
        <div
          id={`system-health-configuration-${page.device.id}`}
          data-age={page.age_years}
          data-status={page.status ?? ''}
          data-device={page.device.id}
          style={{ width: '100%', height: 220 }}
        >
          {word ? <span className="qd-status">{word}</span> : null}
        </div>
      </div>

      <div className="col-md-6">
        <table width="100%">
          <tbody>
            <Fact label="AGE">
              <p>
                {page.age_label}
                {page.age_old ? (
                  <>
                    {' '}
                    <label className="label label-warning">Old</label>
                  </>
                ) : null}
              </p>
            </Fact>
            <Fact label="CPU MODEL">{na(page.cpu)}</Fact>
            <Fact label="RAM">{na(page.ram)}</Fact>
            <Fact label="STORAGE SIZE">{na(page.storage)}</Fact>
            <Fact label="TYPE" muted>
              <p>{na(page.device.type, true)}</p>
            </Fact>
            <Fact label="MANUFACTURER" muted>
              {na(page.device.brand, true)}
            </Fact>
            <Fact label="MODEL" muted>
              {na(page.device.model, true)}
            </Fact>
            <Fact label="SERIAL NUMBER" muted>
              {na(page.serial, true)}
            </Fact>
          </tbody>
        </table>
      </div>

      <div className="col-md-12" style={{ marginTop: 25 }}>
        <h5 className="bordered">
          Events <small>(*last 10)</small>
        </h5>
        {page.events.length === 0 ? (
          <div className="alert alert-info">No events</div>
        ) : (
          <table className="table table-settings">
            <thead>
              <tr>
                <th className="align-left">Date</th>
                <th className="align-left">Message</th>
                <th className="align-left">User</th>
              </tr>
            </thead>
            <tbody>
              {page.events.map((event, i) => (
                <tr key={`${event.date}-${i}`}>
                  <td>{event.date}</td>
                  <td>{event.message}</td>
                  <td>{event.user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Fact({ label, muted, children }: { label: string; muted?: boolean; children: ReactNode }) {
  return (
    <tr>
      <td className="font-10" style={{ borderBottom: '1px solid #DDD', padding: '5px 0' }} width={label === 'STORAGE SIZE' || label === 'MODEL' || label === 'SERIAL NUMBER' ? 150 : undefined}>
        <b>{label}</b>
      </td>
      <td
        style={{
          borderBottom: '1px solid #DDD',
          padding: '5px 0 5px 5px',
          color: muted ? '#AAA' : '#2DB5CC',
          fontWeight: muted ? undefined : 'bold',
        }}
        className="align-left"
      >
        {children}
      </td>
    </tr>
  )
}
